import { getPDFPagesFromFile } from 'PDFUtils';
import FormSwiftAPIClient from 'FormSwiftAPI/FormSwiftAPIClient';
import { startGracefulAutoSave } from 'FormSwiftAPI/Session/SessionAutoSaveActions';
import MessageHandler from 'message-handler';
import PopupEnum from 'Popups/PopupConstants';
import { SELF_SERVE_POPUPS_TO_TOOLBOX_TOOLTIPS } from 'SelfServeTooltipTour/TooltipTourConstants';
import { SelfServeBuilderPopupTypes } from 'Popups';
import { getPopupIdsOnPage } from 'Popups/helpers';
import SelfServePostBuilderSidePanelConstants, { SelfServePostBuilderSidePanelTypes } from 'SelfServePostBuilderSidePanel/SelfServePostBuilderSidePanelConstants';
import SelfServeBuilderEnum from './SelfServeBuilderConstants';
import { SAMPLE_DOCUMENT_TYPES } from './SelfServeBuilderConstants';

const uuidV4 = require('uuid/v4');

const BOX_POPUP_NAME_INPUT_HEIGHT = 38;
const POPUP_ERROR_PADDING = 150;

export const formSwiftAPIClient = new FormSwiftAPIClient(window.API_URL);

export const setPagesFromFile = documentUrl => (
  (dispatch) => {
    getPDFPagesFromFile(documentUrl)
      .then((pages) => {
        dispatch({
          type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_SET_PAGES_FROM_FILE,
          pages,
        });
      });
  }
);

export const setCurrentPage = pageIdx => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_CURRENT_PAGE_SET,
      currentPage: pageIdx,
    });
  }
);

export const setCurrentPagePagination = pageIdx => (
  (dispatch) => {
    // since our pdf viewer has scroll handlers to set the active page, we need
    // to disable them while a user is updating the page through input box so that
    // scroll handlers do no conflict with user input
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_WAYPOINTS_DISABLE,
    });

    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_PAGINATION_CURRENT_PAGE_SET,
      currentPage: pageIdx,
    });

    // after page is done scrolling into view, reenable scroll handlers
    setTimeout(() => {
      dispatch({
        type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_WAYPOINTS_ENABLE,
      });
    }, 100);
  }
);

export const paginateBack = () => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_PAGINATION_BACK,
    });
  }
);

export const paginateNext = () => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_PAGINATION_NEXT,
    });
  }
);

export const enablePopupCreation = () => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_POPUP_CREATION_ENABLED_SET,
      enabled: true,
    });
  }
);

export const disablePopupCreation = () => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_POPUP_CREATION_ENABLED_SET,
      enabled: false,
    });
  }
);

export const setPopupCreationError = () => (
  (dispatch, getState) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_POPUP_CREATION_ATTEMPT_SET,
    });

    const {
      activePopup: activePopupId,
      popups,
    } = getState().popupHandler;

    const activePopupData = popups[activePopupId];

    let popupOffset = activePopupData.y - BOX_POPUP_NAME_INPUT_HEIGHT;
    if (popups[activePopupId].groupId) {
      const groupId = popups[activePopupId].groupId;
      popupOffset = parseInt(document.getElementById(groupId).style.transform.split(',')[1], 10);
    }

    const pageToScrollTo = document.getElementById(`selfServeBuilderPDFPageCanvas${popups[activePopupId].pageIdx}`);

    const topPos = pageToScrollTo.offsetTop + (popupOffset - POPUP_ERROR_PADDING);
    document.getElementById('selfServeBuilderEditorWrapper').scrollTop = topPos;

    // ensure we dispatched an action to set the correct page
    // otherwise, waypoint actions fired from scrolling will set
    // the wrong page number
    setTimeout(() => {
      dispatch({
        type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_CURRENT_PAGE_SET,
        currentPage: popups[activePopupId].pageIdx,
      });
    }, 100);
  }
);

export const getFieldOutlineContentToSave = () => (
  (dispatch, getState) => {
    const state = getState();
    const payload = {
      field_outline: {
        fields: state.popupHandler.popups,
        activePopup: state.popupHandler.activePopup,
        fieldGroups: state.PopupGroupReducer.popupGroups,
        fieldIdsOnPage: getPopupIdsOnPage(state.popupHandler.popups),
        activeGroupId: state.PopupGroupReducer.activeGroupId,
        sections: state.SelfServePostBuilderSidePanelHandler.SectionOrganizerReducer.sections,
        popupGroupsOnPage: state.PopupGroupReducer.popupGroupsOnPage,
      },
      creator_user_id: window.COOKIE.get('userid'),
      creator_user_token: window.COOKIE.get('userToken'),
    };
    return {
      documentType: state.SelfServeBuilderReducer.documentType,
      payload,
    };
  }
);

export const saveFieldOutlineContent = ({ documentType, payload }) => (
  async () => {
    const response = await formSwiftAPIClient.request('POST', `/custom-template/${documentType}`, payload);
    return response.data.custom_template.field_outline;
  }
);

export const createSampleDocument = sampleType => (
  async (dispatch) => {
    const validSampleType = SAMPLE_DOCUMENT_TYPES.indexOf(sampleType) > -1;
    if (validSampleType) {
      const payload = {
        creator_user_id: window.COOKIE.get('userid'),
        creator_user_token: window.COOKIE.get('userToken'),
        sample_type: sampleType,
        version: uuidV4(),
      };
      try {
        const response = await formSwiftAPIClient.request('POST', '/sample-custom-template', payload);
        const documentType = response.data.custom_template.document_type;
        dispatch({
          type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_SAMPLE_DOCUMENT_CREATE_SUCCESS,
          sampleType,
        });
        window.location.assign(`/self-serve-builder?documentType=${documentType}#sample-document`);
      } catch (e) {
        dispatch({
          type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_SAMPLE_DOCUMENT_CREATE_FAILURE,
          sampleType,
        });
        const errorMessage = encodeURI('Could not upload demo document, please try again.');
        window.location.assign(`/myFillableFields.php?error=${errorMessage}`);
      }
    }
  }
);


export const loadCustomTemplateData = (
  documentType,
  isViewOnly = false,
  forceDownload = false,
  activeToolboxItem = SelfServeBuilderPopupTypes.TEXTBOX,
  ) => (
  async (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_CUSTOM_TEMPLATE_DATA_GET_REQUEST,
      documentType,
    });
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_LOADING_START,
      loading: true,
    });

    try {
      const payload = {
        user_id: window.COOKIE.get('userid') || '',
        user_token: window.COOKIE.get('userToken') || '',
        // ie11 caches fetch requests, in order to ensure we fetch latest field_outline
        // (and prevent blowing away data), add uuid to make every request unique
        version: uuidV4(),
      };

      if (isViewOnly) {
        payload.view_only = true;
      }

      if (forceDownload) {
        payload.force_download = true;
        payload.cloudflare_token = window.COOKIE.get('cloudflare_token');
      }

      const response = await formSwiftAPIClient.request('GET', `/custom-template/${documentType}`, payload);
      const responseData = response.data;

      const fields = responseData.custom_template.field_outline.fields;
      const publishedAt = responseData.custom_template.published_at;

      // iterate through fields and find the highest z index
      // we start at zIndex of 3 because popupHandler initially starts at 3
      let highestZIndex = 3;
      if (fields) {
        Object.keys(fields).forEach((fieldId) => {
          if (fields[fieldId].zIndex) {
            highestZIndex = Math.max(highestZIndex, fields[fieldId].zIndex);
          }
        });
      }

      const hasPopups = fields && Object.keys(fields).length > 0;
      // check for cookie here because if they've seen the tooltip tour
      // then they've already created their first popup.
      const isTooltipTourSeen = window.COOKIE.get('preview_tooltip_tour_seen');
      if (hasPopups || isTooltipTourSeen) {
        dispatch({
          type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_FIRST_POPUP_CREATED,
        });
      }

      dispatch({
        type: PopupEnum.SET_Z_INDEX,
        zIndex: highestZIndex,
      });

      dispatch({
        type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_CUSTOM_TEMPLATE_DATA_GET_SUCCESS,
        data: {
          ...responseData,
        },
        activeToolboxItem,
        activeToolboxTourTooltip: SELF_SERVE_POPUPS_TO_TOOLBOX_TOOLTIPS[activeToolboxItem],
      });

      dispatch({
        type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_LOADING_DONE,
        loading: false,
      });

      if (isViewOnly) {
        return;
      }

      if (publishedAt) {
        dispatch({
          type: SelfServePostBuilderSidePanelConstants.DISPLAY_PANEL_TYPE,
          panelType: SelfServePostBuilderSidePanelTypes.RECIPIENT_MANAGER,
        });
      }

      dispatch(startGracefulAutoSave(
        getFieldOutlineContentToSave,
        saveFieldOutlineContent,
        {
          documentType,
          payload: {
            field_outline: responseData.custom_template.field_outline,
            creator_user_id: window.COOKIE.get('userid'),
            creator_user_token: window.COOKIE.get('userToken'),
          },
        },
        3000,
      ));
    } catch (e) {
      dispatch({
        type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_CUSTOM_TEMPLATE_DATA_GET_FAILURE,
        error: e.message,
      });

      window.location.assign(MessageHandler.getErrorRedirectUrl(e.message, true));
    }
  }
);

export const setIsScalingEnabled = (isScalingEnabled = true) => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_SCALING_MODE_SET,
      isScalingEnabled,
    });
  }
);

export const setNotification = (notification, isError = false) => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_NOTIFICATION_SET,
      notification,
      isError,
    });
  }
);

export const duplicateTemplate = documentType => (
  async () => {
    const response = await formSwiftAPIClient.request(
      'POST',
      `/custom-template/${documentType}/duplicate`,
      {
        user_id: window.COOKIE.get('userid'),
        user_token: window.COOKIE.get('userToken'),
      },
    );
    const responseData = response.data;
    return responseData.custom_template.document_type;
  }
);

export const setIsRecipientPreviewModalVisible = isRecipientPreviewModalVisible => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_RECIPIENT_PREVIEW_MODAL_SET,
      isRecipientPreviewModalVisible,
    });
  }
);

export const setZeroState = () => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_LOADING_DONE,
    });
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_ZERO_STATE_SET,
    });
  }
);

export const setSampleState = () => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_SAMPLE_STATE_SET,
    });
  }
);

export const setFirstSamplePopupMounted = () => (
  (dispatch) => {
    dispatch({
      type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_SAMPLE_STATE_FIRST_POPUP_MOUNTED,
    });
  }
);



// WEBPACK FOOTER //
// ./src-react/SelfServeBuilder/SelfServeBuilderActions.jsx